import {computed, inject, Injectable, signal} from '@angular/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {TaxService} from '../../../../api';
import {TaxSummary} from '../../../../api/models';
import {QueryKey} from '../../../constants/query-key';

// Peppol tax category codes used to force the VAT rate according to the customer's zone.
const PEPPOL_CODE = {
  intraCommunity: 'K', // intra-EU supply
  export: 'G',         // export outside the EU
} as const;

@Injectable({providedIn: 'root'})
export class TaxStore {
  private readonly taxService = inject(TaxService);

  private readonly taxQuery = injectQuery(() => ({
    queryKey: [QueryKey.TAXES],
    queryFn: () => lastValueFrom(this.taxService.getTaxes()),
  }));

  private readonly taxSelectQuery = injectQuery(() => ({
    queryKey: [QueryKey.TAXES_SELECT],
    queryFn: () => lastValueFrom(this.taxService.getTaxSelect()),
  }));

  taxSelect = computed(() => this.taxSelectQuery.data()?.taxes ?? []);
  isNationalInvoice = signal(true);
  taxes = computed<TaxSummary[]>(() => this.taxQuery.data()?.taxes ?? []);
  defaultTax = computed(() => this.taxes().find(t => t.isDefault));
  exemptTax = computed(() => this.taxes().find(t => t.peppolCode === 'E'));
  proposedTax = computed(() => this.isNationalInvoice() ? this.defaultTax() : this.exemptTax());

  // Mandatory taxes for foreign customers, matched from the dropdown list (TaxSelect) by Peppol code.
  intraCommunityTax = computed(() => this.taxSelect().find(t => t.peppolCode === PEPPOL_CODE.intraCommunity));
  exportTax = computed(() => this.taxSelect().find(t => t.peppolCode === PEPPOL_CODE.export));
}
